import { useState } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from './Modal';

interface Props {
  /** noun shown in the trigger and modal title, e.g. "category" */
  noun: string;
  /** creates the entity from a name; must resolve to the created object */
  create: (name: string) => Promise<any>;
  /** caller invalidates the relevant query and selects the new item */
  onCreated: (created: any) => void;
  placeholder?: string;
}

/* "+ New category"-style link that pops a minimal name-only dialog,
   creates the entity, and hands it back so the caller can select it —
   no detour to the settings screens mid-ticket. */
export default function QuickAdd({ noun, create, onCreated, placeholder }: Props) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const capNoun = noun.charAt(0).toUpperCase() + noun.slice(1);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const created = await create(name.trim());
      toast.success(`${capNoun} created`);
      setOpen(false);
      setName('');
      onCreated(created);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? `Failed to create ${noun}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline"
      >
        <Plus className="w-3 h-3" /> New {noun}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`New ${noun}`}
        width={380}
        busy={saving}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saving || !name.trim()}>
              {saving ? 'Creating…' : 'Create & select'}
            </button>
          </>
        }
      >
        <input
          autoFocus
          className="input w-full"
          placeholder={placeholder ?? `${capNoun} name`}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
        />
        <p className="text-[11px] text-muted mt-1.5">
          Created with defaults — fine-tune it later in settings.
        </p>
      </Modal>
    </>
  );
}
